/**
 * Author line for a book card, or null when the catalogue has no real author.
 *
 * The catalogue's AUTHOR field (and the WROTE_BY graph behind it) is filled with
 * placeholders ("Unknown author", "NA", "COLLECTIF", "VARIOUS VARIOUS") and, for
 * licensed and workbook titles, the publisher or brand ("DK", "SCHOLASTIC",
 * "RH DISNEY"). Shown as a byline these read as broken data, so they are dropped
 * per contributor: "JAMES PATTERSON, VARIOUS" keeps the person.
 */

const PLACEHOLDER_AUTHORS = new Set([
  '',
  '-',
  'unknown',
  'unknown author',
  'auteur inconnu',
  'inconnu',
  'na',
  'n/a',
  'n.a.',
  'none',
  'null',
  'collectif',
  'collective',
  'various',
  'various authors',
  'various various',
  'divers',
  'divers auteurs',
]);

/**
 * Words that only appear in organisation names, never in a person's byline.
 * Matched against accent-folded text: `\b` treats "é" as a word boundary.
 */
const CORPORATE_MARKERS =
  /\b(publish\w*|publications?|press|presses|books|editions?|editeurs?|editors?|editorial|jeunesse|inc|llc|ltd|pty|corp|corporation|company|group|media|studios?|entertainment|licen[cs]es?|licensing)\b/;

/** Publishers and brands that the catalogue records as the author of licensed titles. */
const PUBLISHER_BRANDS =
  /\b(dk|scholastic|random house|penguin|harpercollins|disney|pixar|marvel|lucasfilm|hasbro|mattel|nickelodeon|lego|pokemon|national geographic|lonely planet|klutz|kumon|workman|usborne|phidal|guinness world records|sports illustrated)\b/;

const CONTRIBUTOR_SEPARATOR = /\s*(?:,|;|&|\band\b|\bet\b)\s*/i;

const normalize = (value: string): string => value.replace(/\s+/g, ' ').trim();

/** The catalogue joins contributors without spaces ("A,B"); cards need "A, B". */
const spaceSeparators = (byline: string): string =>
  byline
    .replace(/\s*([,;])\s*/g, '$1 ')
    .replace(/\s*&\s*/g, ' & ')
    .trim();

const fold = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const isPlaceholder = (name: string): boolean => PLACEHOLDER_AUTHORS.has(fold(name));

const isOrganisation = (name: string): boolean => {
  const folded = fold(name);
  return CORPORATE_MARKERS.test(folded) || PUBLISHER_BRANDS.test(folded);
};

const isRealContributor = (name: string): boolean => !isPlaceholder(name) && !isOrganisation(name);

/**
 * A one-word byline that matches the publisher is the imprint ("DK", "AUZOU").
 * Multi-word matches are left alone: self-published authors list themselves
 * as their own publisher ("MICHAEL REIT").
 */
const isSingleWordPublisher = (author: string, publisher?: string): boolean =>
  !author.includes(' ') &&
  !!publisher &&
  normalize(publisher).toLowerCase() === author.toLowerCase();

export const getDisplayAuthor = (
  author?: string | null,
  publisher?: string | null
): string | null => {
  const byline = normalize(author ?? '');
  if (!byline || isSingleWordPublisher(byline, publisher ?? undefined)) return null;

  const contributors = byline.split(CONTRIBUTOR_SEPARATOR).map(normalize).filter(Boolean);
  const kept = contributors.filter(isRealContributor);
  if (kept.length === 0) return null;
  return kept.length === contributors.length ? spaceSeparators(byline) : kept.join(', ');
};
